import React, { useState } from "react"
import { Link } from "gatsby"
import { useTranslation } from "gatsby-plugin-react-i18next"
import ScheduleForm from "./schedule-form"

const AdoptionFooter = ({ family }) => {
  const { t } = useTranslation()
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false)

  // Dynamic contact information with fallbacks
  const agencyData = family?.agency || {}
  const familyName = family?.name
    ? family.name.replace(" / ", " & ")
    : t('footer.defaultFamilyName')
  const familyTypeText = family.account_type === "Couple" ? t('common.our') : t('common.my')

  // Get IDs for ScheduleForm
  const accountId = family?.mongoId
  const agencyId = family?.agency?.mongoId
  const agencyLogo = agencyData?.avatar ? `${process.env.S3_BUCKET_URL}${agencyData.avatar}` : null

  // Get primary contact info
  let primaryPhone = process.env.PRIMARY_CONTACT_NUMBER
    ? "+1" + process.env.PRIMARY_CONTACT_NUMBER.replace(/\D/g, "")
    : "+13176527696"
  let textNumber = primaryPhone
  let callNumber = primaryPhone
  if (agencyData) {
    if (agencyData.bm_contact) {
      if (agencyData.bm_contact.text) {
        textNumber = `+1${agencyData.bm_contact.text.replace(/\D/g, "")}`
      }
      if (agencyData.bm_contact.phone) {
        callNumber = `+1${agencyData.bm_contact.phone.replace(/\D/g, "")}`
      }
    } else if (agencyData.phone?.length && agencyData.phone[0]?.number) {
      primaryPhone =
        (agencyData.phone[0].country_code
          ? agencyData.phone[0].country_code
          : "+1") + agencyData.phone[0].number.replace(/\D/g, "")
      textNumber = primaryPhone
      callNumber = primaryPhone
    }
  }
  let bmContactEmail =
    process.env.PRIMARY_EMAIL || "greg.phelps@cairsolutions.com"
  if (agencyData?.bm_contact?.email) {
    bmContactEmail = agencyData.bm_contact.email
  } else if (agencyData?.email?.length) {
    bmContactEmail = agencyData.email[0].email
  }

  const agencyWebsite = agencyData?.website || "www.parentfinder.com"
  const agencyName = agencyData?.name || "ParentFinder"

  // Process agency addresses
  const agencyAddresses = agencyData?.address?.length ? agencyData.address : []

  const formatAddress = office => {
    const addressParts = []

    // Build street address
    if (office.line_1) addressParts.push(office.line_1)
    if (office.line_2) addressParts.push(office.line_2)
    if (office.street && !office.line_1) addressParts.push(office.street)

    return {
      street: addressParts.length > 0 ? addressParts.join(", ") : null,
      cityState:
        office.city && office.state?.name
          ? `${office.city}, ${office.state.name}`
          : null,
      zip: office.zip || null,
      phone: office.phone || null,
    }
  }

  // Get formatted addresses or create fallback
  const getDisplayAddresses = () => {
    if (agencyAddresses.length > 0) {
      return agencyAddresses.map((address, index) => ({
        id: index,
        name: address.city || t('footer.office', { number: index + 1 }),
        ...formatAddress(address),
      }))
    }

    // Fallback addresses when no agency addresses available
    return []
  }

  const displayAddresses = getDisplayAddresses()

  const openScheduleModal = () => {
    setIsScheduleModalOpen(true)
  }

  const closeScheduleModal = () => {
    setIsScheduleModalOpen(false)
  }

  return (
    <>
      <footer className="bg-white">
        {/* Contact Agency Section */}
        <div className="py-16 lg:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Section Header */}
            <div className="text-center mb-12">
              <h2 className="text-4xl lg:text-5xl text-gray-700 mb-8 font-heading-main">
                {t('footer.contactAgency', { familyTypeText })}
              </h2>
            </div>

            {/* Main Contact Card */}
            <div className="rounded-3xl p-6 lg:p-8 text-gray-700 shadow-lg mb-8 bg-gray-50">
              <div
                className={`flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-8 ${
                  displayAddresses.length === 1
                    ? "lg:justify-center"
                    : "lg:justify-between"
                }`}
              >
                {/* Left Side - Agency Information */}
                <div className="flex-1">
                  <div className="mb-4">
                    {/* Display logo if available, otherwise agency name */}
                    {agencyLogo ? (
                      <div className="flex items-center mb-4">
                        <img
                          src={agencyLogo}
                          alt={agencyName}
                          className="h-16 lg:h-20 w-auto object-contain"
                          style={{ maxWidth: "250px" }}
                        />
                        {/* <img
                          srcSet={`
                            ${agencyLogo} 300x,
                            ${agencyLogo} 600w,
                            ${agencyLogo} 1200w
                          `}
                          sizes="(max-width: 600px) 150px, (max-width: 1024px) 200px, 250px"
                          src={agencyLogo}
                          alt={agencyName}
                          className="h-16 lg:h-20 w-auto object-contain"
                          style={{ maxWidth: "250px" }}
                        /> */}

                      </div>
                    ) : (
                      <h3 className="text-xl lg:text-2xl font-bold mb-4 font-heading-main">
                        {agencyName}
                      </h3>
                    )}
                  </div>

                  {/* Office Locations - Horizontal Layout */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {displayAddresses.map(office => (
                      <div key={office.id}>
                        <h4 className="text-lg font-semibold mb-1 font-heading-sub">
                          {office.name}
                        </h4>
                        <div className="text-md opacity-90 space-y-0.5 font-body">
                          {office.street && <div>{office.street},</div>}
                          {office.cityState && (
                            <div>
                              {office.cityState}
                              {office.zip && ` ${office.zip}`}
                            </div>
                          )}
                          {office.phone && <div>{office.phone}</div>}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Website */}
                  {agencyWebsite && (
                    <div className="flex items-center">
                      <span
                        className="material-icons mr-2 opacity-90"
                        style={{ fontSize: "20px" }}
                      >
                        language
                      </span>
                      <a
                        href={
                          agencyWebsite.startsWith("http")
                            ? agencyWebsite
                            : `https://${agencyWebsite}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-md opacity-90 hover:opacity-100 transition-opacity duration-200 font-body"
                      >
                        {agencyWebsite}
                      </a>
                    </div>
                  )}
                </div>

                {/* Right Side - Contact Method Cards */}
                <div className="flex-shrink-0">
                  <div className="grid grid-cols-2 gap-3">
                    {/* Call Card */}
                    <a
                      href={`tel:${callNumber}`}
                      className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center hover:bg-white/20 transition-all duration-300 cursor-pointer group hover:scale-105"
                    >
                      <div className="mb-2 flex justify-center">
                        <span
                          className="material-icons"
                          style={{ fontSize: "40px" }}
                        >
                          call
                        </span>
                      </div>
                      <h3 className="text-md font-bold mb-0.5 font-heading-sub">
                        {t('footer.contactMethods.call.title')}
                      </h3>
                      <span className="text-sm opacity-90 font-body">
                        {t('footer.contactMethods.call.description')}
                      </span>
                    </a>

                    {/* Text Card */}
                    <a
                      href={`sms:${textNumber}?body=${encodeURIComponent(t('contact.textMessage', { familyName }))}`}
                      className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center hover:bg-white/20 transition-all duration-300 cursor-pointer group hover:scale-105"
                    >
                      <div className="mb-2 flex justify-center">
                        <span
                          className="material-icons"
                          style={{ fontSize: "40px" }}
                        >
                          textsms
                        </span>
                      </div>
                      <h3 className="text-md font-bold mb-0.5 font-heading-sub">
                        {t('footer.contactMethods.text.title')}
                      </h3>
                      <span className="text-sm opacity-90 font-body">
                        {t('footer.contactMethods.text.description')}
                      </span>
                    </a>

                    {/* Email Card */}
                    <a
                      href={`mailto:${bmContactEmail}`}
                      className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center hover:bg-white/20 transition-all duration-300 cursor-pointer group hover:scale-105"
                    >
                      <div className="mb-2 flex justify-center">
                        <span
                          className="material-icons"
                          style={{ fontSize: "40px" }}
                        >
                          mail
                        </span>
                      </div>
                      <h3 className="text-md font-bold mb-0.5 font-heading-sub">
                        {t('footer.contactMethods.email.title')}
                      </h3>
                      <span className="text-sm opacity-90 font-body">
                        {t('footer.contactMethods.email.description')}
                      </span>
                    </a>

                    {/* Schedule Card - Updated to use modal */}
                    <button
                      onClick={openScheduleModal}
                      className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center hover:bg-white/20 transition-all duration-300 cursor-pointer group hover:scale-105"
                      aria-label={t('footer.contactMethods.schedule.ariaLabel')}
                    >
                      <div className="mb-2 flex justify-center">
                        <span
                          className="material-icons"
                          style={{ fontSize: "40px" }}
                        >
                          event
                        </span>
                      </div>
                      <h3 className="text-md font-bold mb-0.5 font-heading-sub">
                        {t('footer.contactMethods.schedule.title')}
                      </h3>
                      <span className="text-sm opacity-90 font-body">
                        {t('footer.contactMethods.schedule.description')}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Links Section */}
        <div className="py-6 bg-accent">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Footer Links */}
            <div className="text-center">
              <div className="flex flex-wrap items-center justify-center gap-4 text-xs sm:gap-6 sm:text-sm lg:text-base">
                <Link
                  to="https://app.websitepolicies.com/policies/view/ezxrx4se"
                  className="text-gray-700 hover:text-gray-800 transition-colors duration-200 font-body"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('footer.links.cookiePolicy')}
                </Link>
                <Link
                  to="https://app.websitepolicies.com/policies/view/10in95n9"
                  className="text-gray-700 hover:text-gray-800 transition-colors duration-200 font-body"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('footer.links.disclaimer')}
                </Link>
                <Link
                  to="https://app.websitepolicies.com/policies/view/s04ml1o8"
                  className="text-gray-700 hover:text-gray-800 transition-colors duration-200 font-body"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('footer.links.termsConditions')}
                </Link>
                <Link
                  to="https://app.websitepolicies.com/policies/view/v2lusljn"
                  className="text-gray-700 hover:text-gray-800 transition-colors duration-200 font-body"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('footer.links.privacy')}
                </Link>
                <Link
                  to="https://app.websitepolicies.com/policies/view/13nin34z"
                  className="text-gray-700 hover:text-gray-800 transition-colors duration-200 font-body"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('footer.links.acceptableUse')}
                </Link>
              </div>
            </div>

            {/* Copyright */}
            <div className="mt-4 border-t border-gray-600 pt-4">
              <div className="text-center text-xs sm:text-sm text-gray-700 font-body">
                {t('footer.copyright', { year: new Date().getFullYear() })}
              </div>
            </div>
          </div>
        </div>

        {/* Schedule Form Modal */}
        <ScheduleForm
          isOpen={isScheduleModalOpen}
          onClose={closeScheduleModal}
          accountId={accountId}
          agencyId={agencyId}
          familyName={familyName}
        />
      </footer>
    </>
  )
}

export default AdoptionFooter